import { formatPercent, type DashboardTotals } from '@features/analytics/model/dashboard.types';
import { chartColors } from '@shared/lib/chart-theme';

/**
 * Product engagement, as two ratios against their limit.
 *
 * A single ratio is a meter, not a chart — and these two are the ones that say
 * whether growth is real: how many signups ever do the core thing (build a
 * budget), and how many live subscriptions actually pay.
 */
export function EngagementCard({ totals }: { totals: DashboardTotals }) {
  const paidShare =
    totals.activeSubscriptions > 0 ? totals.payingCustomers / totals.activeSubscriptions : 0;

  return (
    <div className="space-y-5">
      <Meter
        label="Activation"
        caption={`${totals.usersWithBudgets} of ${totals.users} users have built a budget`}
        ratio={totals.activationRate}
      />

      <Meter
        label="Paid conversion"
        caption={`${totals.payingCustomers} of ${totals.activeSubscriptions} live subscriptions are on a paid plan`}
        ratio={paidShare}
      />

      <Meter
        label="Churn"
        caption={`${formatPercent(totals.churnRate)} of all subscriptions ever started have ended`}
        ratio={totals.churnRate}
        tone="warning"
      />
    </div>
  );
}

function Meter({
  label,
  caption,
  ratio,
  tone = 'primary',
}: {
  label: string;
  caption: string;
  ratio: number;
  tone?: 'primary' | 'warning';
}) {
  const percent = Math.min(100, Math.max(0, ratio * 100));

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="text-sm text-slate-700">{label}</span>
        <span className="text-sm font-semibold tabular-nums text-slate-900">
          {formatPercent(ratio)}
        </span>
      </div>

      <div
        role="meter"
        aria-valuenow={Math.round(percent)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
        className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100"
      >
        <div
          className="h-full rounded-full"
          style={{
            width: `${percent}%`,
            backgroundColor: tone === 'warning' ? '#eb6834' : chartColors.primary,
          }}
        />
      </div>

      <p className="mt-1 text-xs text-slate-400">{caption}</p>
    </div>
  );
}
